import { Modal, Button, Select, Input, Tag, message } from "antd";
import React, { useEffect, useState, useCallback } from "react";
import {
  categories,
  subCategories,
  processSubCategories,
} from "../../constants/List Constants";
import "../../styles/db.css";
import { getCurrentDateTimeInFormat } from "../../helper/date_helper";
import {
  createTask,
  createTaskRelationship,
  createTaskRelationshipProcess,
} from "../../service/task.service";
import FileToDbTask from "./new/FileToDbTask";
import FileToSpecTask from "./new/FileToSpecTask";
import EventMonitorTask from "./new/EventMonitorTask";
import { addMapping } from "../../service/db.service";
import FileToFileTask from "./new/FileToFileTask";
import DbToDbTask from "./new/DbToDbTask";
import FileMove from "./new/FileMove";
import FileExtraction from "./new/FileExtraction";
import FileManipulation from "./new/FileManipulation";
import TaskTypeConfig from "./TaskTypeConfig";
import "../../styles/taskModal.css";

// ─── Sub-category list lookup ─────────────────────────────────
const SUB_CATEGORY_LISTS = {
  compare: subCategories,
  process: processSubCategories,
};

// ─── Validation rules per sub-category label ─────────────────
const VALIDATORS = {
  "Database to Database": (s) => {
    if (!s.testSuiteIdTwo) return "Test Suite two cannot be empty!";
    if (!s.mappingData) return "Create new or select an existing mapping!";
    return null;
  },
  "File to Specification": (s) => {
    if (!s.matchType) return "Match Type cannot be empty!";
    if (s.matchType !== "any" && !s.matchFileName)
      return "Match File Name cannot be empty!";
    return null;
  },
  "File to Database": (s) => {
    if (!s.mappingData) return "Create new or select an existing mapping!";
    if (!s.matchType) return "Match Type cannot be empty!";
    if (s.matchType !== "any" && !s.matchFileName)
      return "Match File Name cannot be empty!";
    return null;
  },
  "File to File": (s) => {
    if (!s.mappingData) return "Create new or select an existing mapping!";
    if (!s.matchType || !s.matchTypeTwo) return "Match Type cannot be empty!";
    if (s.matchType !== "any" && !s.matchFileName)
      return "Match File Name cannot be empty!";
    return null;
  },
};

// ─── Initial sub-task state ────────────────────────────────────
const INITIAL_SUBTASK = {
  matchType: "",
  matchFileName: "",
  matchTypeTwo: "",
  matchFileNameTwo: "",
  testSuiteId: "",
  testSuiteIdTwo: "",
  mappingData: null,
  mappingName: "",
  mappingType: "",
  delimiter: "",
  delimiterTwo: "",
  sqlTrigger: "",
  envName: "",
  dbName: "",
  eventType: "",
  pollIntervalSeconds: 10,
  pollTimeoutSeconds: 300,
};

function TaskModal(props) {
  const { TextArea } = Input;

  // Core task fields
  const [taskName, setTaskName] = useState("");
  const [categoryKey, setCategoryKey] = useState("");
  const [categoryLabel, setCategoryLabel] = useState("");
  const [subCategoryKey, setSubCategoryKey] = useState("");
  const [subCategoryLabel, setSubCategoryLabel] = useState("");
  const [comments, setComments] = useState("");
  const [inputStatus, setInputStatus] = useState("");
  const [saveDisabled, setSaveDisabled] = useState(false);
  // Inline errors for the modal-owned required fields (identity + type). Deep
  // per-type field validation stays on the existing VALIDATORS toasts.
  const [errors, setErrors] = useState({});
  // True while a compare sub-task has its mapping editor open — the modal swaps
  // its body to the mapping editor in place (no modal-on-modal).
  const [mapOpen, setMapOpen] = useState(false);
  const [testSuiteList, setTestSuiteList] = useState([]);

  // All sub-task data in one unified object
  const [subTask, setSubTask] = useState(INITIAL_SUBTASK);

  // Shared mapping UI state
  const [mappingSelection, setMappingSelection] = useState([]);
  const [mapName, setMapName] = useState("");
  const [mappedData, setMappedData] = useState([]);
  const [mappedList, setMappedList] = useState([]);
  const [matchTypeOne, setMatchTypeOne] = useState("");

  // Filter test suites by type
  const filterTestSuites = useCallback(
    (value) => {
      const suiteTypes = {
        file2spec: "Specification",
        file2Db: "Database",
        db2Db: "Database",
      };
      const type = suiteTypes[value];
      setTestSuiteList(
        type
          ? (props.suiteData || []).filter((i) => i.testSuiteType === type)
          : [],
      );
    },
    [props.suiteData],
  );

  // Sync props when opened from context (e.g. pre-selected category)
  useEffect(() => {
    if (props.subCategoryKey) {
      setSubCategoryKey(props.subCategoryKey);
      filterTestSuites(props.subCategoryKey);
    }
    if (props.subCategoryValue) setSubCategoryLabel(props.subCategoryValue);
    if (props.categoryKey) setCategoryKey(props.categoryKey);
    if (props.categoryValue) setCategoryLabel(props.categoryValue);
  }, [
    props.subCategoryKey,
    props.subCategoryValue,
    props.categoryKey,
    props.categoryValue,
    filterTestSuites,
  ]);

  // Tag render for label multi-select
  const tagRender = ({ label, closable, onClose }) => {
    const onPreventMouseDown = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    return (
      <Tag
        color="green"
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        style={{ marginRight: 3 }}
      >
        {label}
      </Tag>
    );
  };

  // Reset and close
  const handleModal = () => {
    setTaskName("");
    setInputStatus("");
    setCategoryKey("");
    setCategoryLabel("");
    setSubCategoryKey("");
    setSubCategoryLabel("");
    setComments("");
    setSubTask(INITIAL_SUBTASK);
    setMappingSelection([]);
    setMapName("");
    setMappedData([]);
    setMappedList([]);
    setErrors({});
    setMapOpen(false);
    props.openModal(false);
  };

  // Generic sub-task data saver — merges partial updates into subTask
  const saveSubTaskData = useCallback((data) => {
    setSubTask((prev) => ({ ...prev, ...data }));
  }, []);

  // Adapter callbacks — keep existing sub-component prop signatures working
  const saveFileToDbData = (
    mapping,
    mappingName,
    testSuite,
    match,
    fileName,
    delimiter,
    mapType,
  ) =>
    saveSubTaskData({
      mappingData: mapping,
      mappingName,
      testSuiteId: testSuite,
      matchType: match,
      matchFileName: fileName,
      delimiter,
      mappingType: mapType,
    });

  const saveFileToFileData = (
    mapping,
    mappingName,
    matchOne,
    matchTwo,
    fileNameOne,
    fileNameTwo,
    delimiter,
    delimiterTwo,
    mapType,
  ) =>
    saveSubTaskData({
      mappingData: mapping,
      mappingName,
      matchType: matchOne,
      matchFileName: fileNameOne,
      matchTypeTwo: matchTwo,
      matchFileNameTwo: fileNameTwo,
      delimiter,
      delimiterTwo,
      mappingType: mapType,
    });

  const saveDbToDbData = (mapping, mappingName, suiteOne, suiteTwo, mapType) =>
    saveSubTaskData({
      mappingData: mapping,
      mappingName,
      testSuiteId: suiteOne,
      testSuiteIdTwo: suiteTwo,
      mappingType: mapType,
    });

  const saveEventMonitorData = (data) =>
    saveSubTaskData({
      matchType: data.matchType,
      matchFileName: data.fileName,
      envName: data.environmentName,
      dbName: data.dbConnection,
      sqlTrigger: data.sql,
      eventType: data.eventType,
      pollIntervalSeconds: data.pollIntervalSeconds || 10,
      pollTimeoutSeconds: data.pollTimeoutSeconds || 300,
    });

  // FileMove and FileExtraction share the same shape as event monitor
  const saveFileMoveData = saveEventMonitorData;
  const saveFileExtractData = saveEventMonitorData;

  // Validation — identity + type errors render inline; the deep per-type field
  // rules stay on VALIDATORS toasts (sub-component inputs unchanged).
  const validate = () => {
    const errs = {};
    if (inputStatus === "error") {
      errs.taskName = `${taskName} already exists. Please choose another name.`;
    } else if (!taskName || taskName.trim() === "") {
      errs.taskName = "Task Name cannot be empty!";
    }
    if (!categoryKey) errs.category = "Choose a task type.";
    if (!subCategoryKey) errs.subCategory = "Choose a sub-type.";
    setErrors(errs);
    if (Object.keys(errs).length > 0) return false;

    const validator = VALIDATORS[subCategoryLabel];
    if (validator) {
      const error = validator(subTask);
      if (error) {
        message.error(error);
        return false;
      }
    }
    return true;
  };

  // Task creation
  const onClickSave = () => {
    if (!validate()) return;
    setSaveDisabled(true);

    createTask(
      taskName,
      categoryLabel,
      subCategoryLabel,
      subTask.testSuiteId,
      comments,
      getCurrentDateTimeInFormat(),
      subTask.testSuiteIdTwo,
    )
      .then((res) => {
        if (res.status !== 200) return;
        const taskId = res.data.data[0];

        if (categoryLabel.toLowerCase() === "compare") {
          return createTaskRelationship(
            subCategoryKey,
            taskId,
            subTask.mappingName,
            subTask.matchType,
            subTask.matchFileName,
            subTask.matchTypeTwo,
            subTask.matchFileNameTwo,
            subTask.delimiter,
            subTask.delimiterTwo,
          ).then((relnRes) => {
            if (relnRes.status !== 200) return;
            if (subTask.mappingType === "new") {
              return addMapping(subTask.mappingData).then(() => {
                message.success(
                  "Task created successfully and new mapping created",
                );
                onSuccess(taskId);
              });
            }
            message.success("Task created successfully");
            onSuccess(taskId);
          });
        }

        if (categoryLabel.toLowerCase() === "process automation") {
          return createTaskRelationshipProcess(
            taskId,
            subTask.matchType,
            subTask.matchFileName,
            subTask.sqlTrigger,
            subTask.envName,
            subTask.dbName,
            subTask.eventType,
            subTask.pollIntervalSeconds,
            subTask.pollTimeoutSeconds,
          ).then(() => {
            message.success("Task created successfully");
            onSuccess(taskId);
          });
        }
      })
      .catch((err) =>
        // Prefer the backend's reason (e.g. "a task named X already exists in another
        // project") over axios's generic "Request failed with status code 400".
        message.error(
          err.response?.data?.message ||
            `Error creating task: ${err.message || err.code}`,
          8,
        ),
      )
      .finally(() => setSaveDisabled(false));
  };

  const onSuccess = (taskId) => {
    props.onTaskCreated({
      taskId,
      taskName,
      category: categoryLabel,
      subCategory: subCategoryLabel,
    });
    props.refreshTaskData();
    handleModal();
  };

  const onChangeCategory = (value, obj) => {
    setCategoryKey(value);
    // obj.raw is the underlying stored label ("Compare"); obj.label is now the
    // display text ("Reconciliation"). Persist the raw value — data unchanged.
    setCategoryLabel(obj.raw ?? obj.label);
    setSubCategoryKey("");
    setSubCategoryLabel("");
  };

  const onChangeSubCategory = (value, obj) => {
    setSubCategoryKey(value);
    setSubCategoryLabel(obj.raw ?? obj.label);
    filterTestSuites(value);
    setSubTask(INITIAL_SUBTASK);
    setMappingSelection([]);
    setMapName("");
    setMappedData([]);
  };

  // Sub-task component registry — one lookup, no switch
  const COMPONENTS = {
    file2spec: (
      <FileToSpecTask
        testSuiteList={testSuiteList}
        updateMatchType={(v) => saveSubTaskData({ matchType: v })}
        updateMatchFileName={(v) => saveSubTaskData({ matchFileName: v })}
        updateTestSuiteId={(v) => saveSubTaskData({ testSuiteId: v })}
        updateTestSuiteName={() => {}}
      />
    ),
    file2Db: (
      <FileToDbTask
        onMapViewChange={setMapOpen}
        updateMatchType={(v) => saveSubTaskData({ matchType: v })}
        testSuites={testSuiteList}
        setSqlHeader={(setSqlHeader) => setSqlHeader}
        saveFileToDbData={saveFileToDbData}
        mappingSelection={mappingSelection}
        setMappingSelection={setMappingSelection}
        mapName={mapName}
        setMapName={setMapName}
        mappedData={mappedData}
        setMappedData={setMappedData}
        setMappedList={setMappedList}
        mappedList={mappedList}
      />
    ),
    file2File: (
      <FileToFileTask
        onMapViewChange={setMapOpen}
        saveFileToFileData={saveFileToFileData}
        mappingSelection={mappingSelection}
        setMappingSelection={setMappingSelection}
        mapName={mapName}
        setMapName={setMapName}
        mappedData={mappedData}
        setMappedData={setMappedData}
        setMappedList={setMappedList}
        mappedList={mappedList}
        updateMatchType={(v) => saveSubTaskData({ matchType: v })}
        matchTypeOne={matchTypeOne}
        setMatchTypeOne={setMatchTypeOne}
        matchTypeTwo={subTask.matchTypeTwo}
        setMatchTypeTwo={(v) => saveSubTaskData({ matchTypeTwo: v })}
      />
    ),
    db2Db: (
      <DbToDbTask
        onMapViewChange={setMapOpen}
        testSuites={testSuiteList}
        saveDbToDbData={saveDbToDbData}
        mappingSelection={mappingSelection}
        setMappingSelection={setMappingSelection}
        mapName={mapName}
        setMapName={setMapName}
        mappedData={mappedData}
        setMappedData={setMappedData}
        setMappedList={setMappedList}
        mappedList={mappedList}
      />
    ),
    eventMonitor: (
      <EventMonitorTask saveEventMonitorData={saveEventMonitorData} />
    ),
    move: <FileMove saveFileMoveData={saveFileMoveData} />,
    extractCompressed: (
      <FileExtraction
        saveFileExtractData={saveFileExtractData}
        updateMatchType={(v) => saveSubTaskData({ matchType: v })}
      />
    ),
    fileManipulation: <FileManipulation />,
  };

  // categoryKey arrives as the value ("compare") from a card click, but as the
  // label ("Compare") when the modal is opened pre-filled from a Master Suite
  // step. Normalise to the value so the right card highlights in both flows.
  const effectiveCategoryKey =
    categories.find((c) => c.value === categoryKey || c.label === categoryKey)
      ?.value || "";

  return (
    <Modal
      open={props.isOpen}
      destroyOnClose
      maskClosable={false}
      centered
      width="880px"
      footer={
        mapOpen
          ? null
          : [
              <Button key="cancel" onClick={handleModal}>
                Cancel
              </Button>,
              <Button
                key="save"
                type="primary"
                onClick={onClickSave}
                disabled={saveDisabled}
              >
                Save
              </Button>,
            ]
      }
      onCancel={handleModal}
    >
      {!mapOpen && (
        <p className="modal-header-text" style={{ marginBottom: 20 }}>
          Create Task
        </p>
      )}

      <div className={`task-modal-body${mapOpen ? " task-modal-body--map" : ""}`}>
        {/* LEFT — identity. Hidden (not unmounted) while the mapping editor is
            open so the uncontrolled Task Name / Comments inputs keep their values. */}
        <div
          className="task-modal-left"
          style={mapOpen ? { display: "none" } : undefined}
        >
          <div className="tm-field">
            <span className="tm-label">Task Name *</span>
            <Input
              placeholder="task name"
              onChange={(e) => {
                setTaskName(e.target.value);
                setInputStatus(
                  props.taskNames.includes(e.target.value) ? "error" : "",
                );
                if (errors.taskName)
                  setErrors((prev) => ({ ...prev, taskName: undefined }));
              }}
              status={inputStatus || (errors.taskName ? "error" : "")}
              maxLength={255}
            />
            {inputStatus === "error" ? (
              <p className="tm-error">Task name already exists!</p>
            ) : (
              errors.taskName && <p className="tm-error">{errors.taskName}</p>
            )}
          </div>

          <div className="tm-field">
            <span className="tm-label">Select Label(s)</span>
            <Select
              mode="multiple"
              tagRender={tagRender}
              style={{ width: "100%" }}
              options={[{ value: "not implemented" }]}
              placeholder="select label(s)"
            />
          </div>

          <div className="tm-field">
            <span className="tm-label">Comments</span>
            <TextArea
              placeholder="comment/description"
              autoSize={{ minRows: 3, maxRows: 5 }}
              onChange={(e) => setComments(e.target.value)}
            />
          </div>
        </div>

        {/* RIGHT — task type + sub-type + type-specific fields (full width
            while the mapping editor is open) */}
        <div className="task-modal-right">
          <TaskTypeConfig
            categoryKey={effectiveCategoryKey}
            subCategoryKey={subCategoryKey}
            categoryOptions={categories}
            subCategoryOptions={SUB_CATEGORY_LISTS[effectiveCategoryKey] || []}
            disabledCategory={props.disabledCategory || false}
            disabledSubCategory={props.disabledSubCategory || false}
            onSelectCategory={(value, rawLabel) => {
              onChangeCategory(value, { raw: rawLabel });
              setErrors((prev) => ({ ...prev, category: undefined }));
            }}
            onSelectSubCategory={(value, rawLabel) => {
              onChangeSubCategory(value, { raw: rawLabel });
              setErrors((prev) => ({ ...prev, subCategory: undefined }));
            }}
            renderFields={(key) => COMPONENTS[key] || null}
            errors={errors}
            fieldsOnly={mapOpen}
          />
        </div>
      </div>
    </Modal>
  );
}

export default TaskModal;
